/**
 * DriverDocumentsPanel — driver-side upload + status surface for the
 * four required compliance documents (Licence / Badge / Insurance /
 * Vehicle). Phase 1 Slice 2 launch-readiness.
 *
 * Strict isolation contract:
 *   - Pure UI surface — no dispatch / state-machine / janitor /
 *     offer-routing / auth code is touched here.
 *   - File is read locally via FileReader → base64 → posted as JSON
 *     so the existing API gateway doesn't need multipart support.
 *   - One card per required doc. Replace / Resubmit / Renew re-opens the
 *     file + expiry inputs; the backend archives the superseded version, and a
 *     renewal of an approved doc stays separate until an admin approves it.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { FileText, CheckCircle2, Clock, XCircle, Upload, RefreshCw, CalendarClock } from "lucide-react";

const DOC_LABELS = {
  licence: { title: "Driver's Licence", subtitle: "DVLA photo card — add front & back (we combine them)" },
  badge:   { title: "Private Hire Driver Badge", subtitle: "Council-issued private hire driver badge" },
  insurance:{ title: "Hire & Reward Insurance", subtitle: "Current certificate of insurance" },
  vehicle_licence: { title: "Private Hire Vehicle Licence", subtitle: "Council-issued vehicle licence — expiry tracked" },
  vehicle_logbook: { title: "Vehicle Log Book (V5C)", subtitle: "DVLA V5C registration certificate for your vehicle" },
  photo:   { title: "Driver Photo", subtitle: "Clear, recent headshot — image only (no PDF)" },
  right_to_work: { title: "Right to Work", subtitle: "Passport, visa or share code proving your right to work in the UK" },
  dbs:     { title: "DBS Check", subtitle: "Enhanced DBS certificate (criminal records check)" },
  mot:     { title: "MOT Certificate", subtitle: "Current MOT pass certificate for your vehicle" },
};
const DOC_ORDER = ["licence", "badge", "right_to_work", "dbs", "photo", "mot", "insurance", "vehicle_licence", "vehicle_logbook"];
// Document types that carry an expiry date. Photo (identity shot) and the V5C
// logbook (never expires) are excluded.
const EXPIRING_TYPES = ["licence", "badge", "insurance", "right_to_work", "dbs", "mot", "vehicle_licence"];

// Client-side mirror of services/compliance.doc_status for inline display.
const expiryStatus = (iso) => {
  if (!iso) return { status: "unset", days: null };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(`${iso}T00:00:00`);
  if (isNaN(exp.getTime())) return { status: "unset", days: null };
  const days = Math.round((exp - today) / 86400000);
  if (days < 0) return { status: "expired", days };
  if (days <= 30) return { status: "expiring", days };
  return { status: "valid", days };
};
const EXPIRY_STYLE = {
  expired:  { fg: "#991B1B", bg: "#FEE2E2", label: "EXPIRED" },
  expiring: { fg: "#92400E", bg: "#FEF3C7", label: "EXPIRING SOON" },
  valid:    { fg: "#065F46", bg: "#D1FAE5", label: "VALID" },
  unset:    { fg: "#374151", bg: "#F3F4F6", label: "NO EXPIRY SET" },
};

// Mirror the backend whitelist exactly. Defence-in-depth: backend will
// reject anything outside this list, but rejecting locally first means
// the user doesn't waste time uploading an 8 MB ZIP.
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 8 * 1024 * 1024;

const STATUS_COLOR = {
  approved: { bg: "#ECFDF5", border: "#10B981", text: "#065F46", label: "APPROVED", Icon: CheckCircle2 },
  pending:  { bg: "#FFFBEB", border: "#F59E0B", text: "#92400E", label: "UNDER REVIEW", Icon: Clock },
  rejected: { bg: "#FEF2F2", border: "#EF4444", text: "#991B1B", label: "REJECTED", Icon: XCircle },
  missing:  { bg: "#F9FAFB", border: "#E5E7EB", text: "#6B7280", label: "NOT UPLOADED", Icon: Upload },
};

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      // result looks like "data:image/jpeg;base64,/9j/4AAQ..."; strip the prefix.
      const idx = String(result).indexOf(",");
      resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Combine two images into ONE vertically-stacked JPEG, entirely client-side,
// so an ordinary driver can submit licence front + back without any external
// editing tool. Re-encodes under the 8 MB cap. Licence-only; other docs are
// unaffected and the backend still stores a single file per doc_type (the
// hardened 5/5 compliance gate is untouched).
const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });

const combineVertical = async (frontFile, backFile) => {
  const [a, b] = await Promise.all([loadImage(frontFile), loadImage(backFile)]);
  const targetW = Math.min(1600, Math.max(a.width, b.width)); // cap width to control size
  const aH = Math.round((a.height / a.width) * targetW);
  const bH = Math.round((b.height / b.width) * targetW);
  const gap = 16;
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = aH + gap + bH;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(a, 0, 0, targetW, aH);
  ctx.drawImage(b, 0, aH + gap, targetW, bH);
  let quality = 0.85;
  let blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  while (blob && blob.size > MAX_BYTES && quality > 0.4) {
    quality -= 0.15;
    // eslint-disable-next-line no-await-in-loop
    blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  }
  return blob;
};

export const DriverDocumentsPanel = ({ renewDocType = null }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);
  // Licence-only: front/back photos selected for client-side combine.
  const [licenceFront, setLicenceFront] = useState(null);
  const [licenceBack, setLicenceBack] = useState(null);
  // Driver-entered dates keyed by doc_type: { [type]: { expiry, issue } }.
  // Captured BEFORE the file is chosen so they ride along with the upload.
  const [dateDrafts, setDateDrafts] = useState({});
  // Doc types the driver has opened for Replace / Resubmit / Renew.
  const [editing, setEditing] = useState({});

  const stopEditing = (docType) => {
    setEditing((s) => ({ ...s, [docType]: false }));
    setDateDrafts((s) => ({ ...s, [docType]: {} }));
    if (docType === "licence") { setLicenceFront(null); setLicenceBack(null); }
  };
  const submittedToast = (docType, res) =>
    toast.success(res?.data?.renewal
      ? `${DOC_LABELS[docType].title} renewal submitted — your current approved document stays active until it's reviewed.`
      : `${DOC_LABELS[docType].title} submitted — awaiting review.`);

  const setDate = (docType, key, value) =>
    setDateDrafts((s) => ({ ...s, [docType]: { ...s[docType], [key]: value } }));

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/driver/documents");
      setDocs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchDocs(); }, [fetchDocs]);
  // Opened from the expiry banner's Renew button: open that card's form and scroll to it.
  useEffect(() => {
    const type = renewDocType?.type;
    if (!type) return;
    setEditing((s) => ({ ...s, [type]: true }));
    const t = setTimeout(() => document.querySelector(`[data-testid="doc-card-${type}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    return () => clearTimeout(t);
  }, [renewDocType]);

  const onSelectFile = async (docType, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Clear the input so re-selecting the same file fires onChange again
    event.target.value = "";

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, WebP, or PDF.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("File is too large. Maximum 8 MB per document.");
      return;
    }
    // Expiry date is mandatory for expiring compliance documents.
    const draft = dateDrafts[docType] || {};
    if (EXPIRING_TYPES.includes(docType) && !draft.expiry) {
      const lbl = docType === "dbs" ? "renewal date" : "expiry date";
      toast.error(`Please enter the ${lbl} before uploading this document.`);
      return;
    }
    setUploadingType(docType);
    try {
      const data = await fileToBase64(file);
      const res = await api.post("/driver/documents", {
        doc_type: docType,
        filename: file.name,
        content_type: file.type,
        data,
        ...(EXPIRING_TYPES.includes(docType) && draft.expiry ? { expiry_date: draft.expiry } : {}),
        ...(docType === "dbs" && draft.issue ? { issue_date: draft.issue } : {}),
      });
      submittedToast(docType, res);
      stopEditing(docType);
      fetchDocs();
    } catch (e) {
      toast.error(formatApiError(e) || "Upload failed");
    } finally {
      setUploadingType(null);
    }
  };

  // Stable ordered list — backend pads missing types but order isn't guaranteed.
  const ordered = DOC_ORDER.map(
    (t) => docs.find((d) => d.doc_type === t) || { doc_type: t, status: "missing" },
  );

  const onSelectLicenceSide = (setter, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      toast.error("Please choose a JPG, PNG, or WebP image for the licence sides.");
      return;
    }
    setter(file);
  };

  const uploadLicenceFrontBack = async () => {
    if (!licenceFront || !licenceBack) return;
    const draft = dateDrafts["licence"] || {};
    if (!draft.expiry) {
      toast.error("Please enter the expiry date before uploading your licence.");
      return;
    }
    setUploadingType("licence");
    try {
      const blob = await combineVertical(licenceFront, licenceBack);
      if (!blob) throw new Error("Could not combine the two photos.");
      if (blob.size > MAX_BYTES) {
        toast.error("Combined image is still too large. Please use smaller photos.");
        return;
      }
      const data = await fileToBase64(blob);
      const res = await api.post("/driver/documents", {
        doc_type: "licence",
        filename: "licence-front-back.jpg",
        content_type: "image/jpeg",
        data,
        ...(draft.expiry ? { expiry_date: draft.expiry } : {}),
      });
      submittedToast("licence", res);
      stopEditing("licence");
      fetchDocs();
    } catch (e) {
      toast.error(formatApiError(e) || "Upload failed");
    } finally {
      setUploadingType(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="driver-documents-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-black tracking-tight">Compliance documents</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Upload each required document. An operator will review and notify you.
          </p>
        </div>
        <Button
          variant="ghost" size="sm" onClick={fetchDocs} disabled={loading}
          data-testid="documents-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {ordered.map((doc) => {
          const meta = DOC_LABELS[doc.doc_type];
          const sty = STATUS_COLOR[doc.status] || STATUS_COLOR.missing;
          const SIcon = sty.Icon;
          const isUploading = uploadingType === doc.doc_type;
          const isEditing = doc.status === "missing" || !!editing[doc.doc_type];
          const ren = doc.renewal;
          const actionLabel = doc.status === "rejected" ? "Resubmit document"
            : doc.status === "pending" ? "Replace submission"
              : ren ? "Replace renewal" : "Renew / replace";
          return (
            <div
              key={doc.doc_type}
              className="border-l-4 p-3 bg-card"
              style={{ borderLeftColor: sty.border, backgroundColor: sty.bg }}
              data-testid={`doc-card-${doc.doc_type}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{meta.title}</div>
                  <div className="text-[11px] text-muted-foreground">{meta.subtitle}</div>
                </div>
                <span
                  className="label-mini flex items-center gap-1 flex-shrink-0"
                  style={{ color: sty.text }}
                  data-testid={`doc-status-${doc.doc_type}`}
                >
                  <SIcon className="w-3 h-3" />
                  {sty.label}
                </span>
              </div>

              {doc.filename && (
                <div className="text-[11px] text-muted-foreground font-mono-tab truncate" title={doc.filename}>
                  <FileText className="w-3 h-3 inline mr-1" />
                  {doc.filename} · {fmtSize(doc.size_bytes)}
                </div>
              )}

              {doc.status === "rejected" && doc.rejection_reason && (
                <div className="mt-2 text-[11px] text-[#991B1B] bg-white/50 p-2 border border-[#FCA5A5]">
                  <strong>Reviewer note:</strong> {doc.rejection_reason}
                </div>
              )}

              {/* Driver-entered expiry (and DBS issue) date. Captured at upload
                  time and shown back once stored so the driver can see what's
                  on file. Optional — admins can still set/correct it later. */}
              {EXPIRING_TYPES.includes(doc.doc_type) && (() => {
                const draft = dateDrafts[doc.doc_type] || {};
                const expLabel = doc.doc_type === "dbs" ? "Renewal date" : "Expiry date";
                const st = EXPIRY_STYLE[expiryStatus(doc.expiry_date).status];
                return (
                  <div className="mt-2 space-y-1.5" data-testid={`doc-dates-${doc.doc_type}`}>
                    {doc.expiry_date && isEditing && (
                      <div className="text-[11px] text-muted-foreground" data-testid={`doc-current-expiry-${doc.doc_type}`}>
                        On file: <span className="font-mono-tab">{doc.expiry_date}</span> — enter the new {expLabel.toLowerCase()} below.
                      </div>
                    )}
                    {doc.expiry_date && !isEditing ? (
                      <div className="flex items-center gap-1.5 text-[11px]">
                        <span className="text-muted-foreground">{expLabel}:</span>
                        <span className="font-mono-tab font-semibold">{doc.expiry_date}</span>
                        <span
                          className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded-sm"
                          style={{ color: st.fg, background: st.bg }}
                          data-testid={`doc-expiry-status-${doc.doc_type}`}
                        >
                          {st.label}
                        </span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-1.5">
                        {doc.doc_type === "dbs" && (
                          <label className="flex items-center gap-1.5 text-[11px]">
                            <CalendarClock className="w-3 h-3 text-muted-foreground" />
                            <span className="text-muted-foreground w-20">Issue date</span>
                            <input
                              type="date"
                              value={draft.issue || ""}
                              onChange={(e) => setDate(doc.doc_type, "issue", e.target.value)}
                              className="border border-border px-2 py-1 text-[11px] font-mono-tab focus:outline-none focus:border-[#0A0A0A] flex-1"
                              data-testid={`doc-issue-input-${doc.doc_type}`}
                            />
                          </label>
                        )}
                        <label className="flex items-center gap-1.5 text-[11px]">
                          <CalendarClock className="w-3 h-3 text-muted-foreground" />
                          <span className="text-muted-foreground w-20">{expLabel}</span>
                          <input
                            type="date"
                            value={draft.expiry || ""}
                            onChange={(e) => setDate(doc.doc_type, "expiry", e.target.value)}
                            className="border border-border px-2 py-1 text-[11px] font-mono-tab focus:outline-none focus:border-[#0A0A0A] flex-1"
                            data-testid={`doc-expiry-input-${doc.doc_type}`}
                          />
                        </label>
                        <p className="text-[10px] text-muted-foreground">
                          Enter the {expLabel.toLowerCase()} before choosing your file.
                        </p>
                      </div>
                    )}
                    {doc.doc_type === "dbs" && doc.issue_date && (
                      <div className="text-[11px]">
                        <span className="text-muted-foreground">Issue date:</span>{" "}
                        <span className="font-mono-tab font-semibold">{doc.issue_date}</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {ren && (
                <div
                  className="mt-2 text-[11px] p-2 border bg-white/60"
                  style={{ borderColor: ren.status === "rejected" ? "#FCA5A5" : "#FCD34D" }}
                  data-testid={`doc-renewal-${doc.doc_type}`}
                >
                  <strong>{ren.status === "rejected" ? "Renewal rejected" : "Renewal awaiting review"}</strong>
                  {ren.expiry_date && <> · new expiry <span className="font-mono-tab">{ren.expiry_date}</span></>}
                  {ren.status === "rejected" && ren.rejection_reason && <div className="text-[#991B1B]">Reviewer note: {ren.rejection_reason}</div>}
                  <div className="text-muted-foreground">Your current approved document stays active until a renewal is approved.</div>
                </div>
              )}

              {!isEditing ? (
                <Button
                  size="sm" variant="outline" className="mt-2 w-full"
                  onClick={() => setEditing((s) => ({ ...s, [doc.doc_type]: true }))}
                  data-testid={`doc-resubmit-btn-${doc.doc_type}`}
                >
                  <Upload className="w-3.5 h-3.5 mr-1" /> {actionLabel}
                </Button>
              ) : doc.doc_type === "licence" ? (
                <div className="mt-2 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {[["Front", licenceFront, setLicenceFront], ["Back", licenceBack, setLicenceBack]].map(
                      ([side, val, setVal]) => (
                        <label
                          key={side}
                          className="flex flex-col items-center justify-center gap-0.5 border border-dashed border-border p-2 text-[11px] cursor-pointer hover:bg-secondary/40 text-center"
                          data-testid={`doc-upload-licence-${side.toLowerCase()}`}
                        >
                          <Upload className="w-3.5 h-3.5" />
                          <span className="font-semibold">{side}</span>
                          <span className="text-muted-foreground truncate max-w-full">
                            {val ? val.name : "Choose photo"}
                          </span>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                            className="hidden"
                            disabled={isUploading}
                            onChange={(e) => onSelectLicenceSide(setVal, e)}
                          />
                        </label>
                      ),
                    )}
                  </div>
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={!licenceFront || !licenceBack || isUploading || !dateDrafts["licence"]?.expiry}
                    onClick={uploadLicenceFrontBack}
                    data-testid="doc-upload-licence-combine-btn"
                  >
                    {isUploading ? "Uploading…" : "Upload licence (front + back)"}
                  </Button>
                  <label
                    className="block text-center text-[11px] text-muted-foreground cursor-pointer hover:underline"
                    data-testid="doc-upload-licence-single"
                  >
                    …or upload a single file (PDF, or both sides in one image)
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
                      className="hidden"
                      disabled={isUploading}
                      onChange={(e) => onSelectFile("licence", e)}
                    />
                  </label>
                </div>
              ) : (
                <div className="mt-2">
                  <label className="inline-flex items-center gap-1.5 text-xs font-semibold cursor-pointer hover:underline" data-testid={`doc-upload-${doc.doc_type}`}>
                    <Upload className="w-3.5 h-3.5" />
                    {isUploading ? "Uploading…" : doc.status === "missing" ? "Upload" : "Choose new file"}
                    <input
                      type="file"
                      accept={doc.doc_type === "photo"
                        ? ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        : ".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"}
                      className="hidden"
                      disabled={isUploading}
                      onChange={(e) => onSelectFile(doc.doc_type, e)}
                    />
                  </label>
                </div>
              )}
              {isEditing && doc.status !== "missing" && (
                <button
                  type="button" className="mt-1 text-[11px] text-muted-foreground hover:underline"
                  onClick={() => stopEditing(doc.doc_type)} disabled={isUploading}
                  data-testid={`doc-resubmit-cancel-${doc.doc_type}`}
                >
                  Cancel
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DriverDocumentsPanel;
